import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: number | '' | string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  uniqueKey?: string;
  maxLength?: number;
  id?: string
}

const InputContainer = styled.div<{ inputType?: string }>`
  position: relative;
  max-width: max-content;
`;

const StyledInput = styled.input<{ inputType?: string; inputLength: number }>`
  width: 100%;
  padding: 8px 12px;
  text-align: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
  color: ${({ theme }) => theme.playerColor};
  background: ${({ theme }) => theme.notificationTitleColor};
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "DM Mono";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.playerDataBorder};
    box-shadow: none;
  }
`;

const NumberTextField: React.FC<NumberInputProps> = ({ value: initialValue, onChange, type, maxLength, id, ...props }) => {

  const isHeightCm = id === "Height Cm";
  const inputRef = useRef<HTMLInputElement | null>(null);

  const formatDecimal = (value: number | string, isHeightCm: boolean) => {
      let numStr = String(value).replace(/[^\d]/g, ""); // Remove non-numeric characters
      if (isHeightCm) {
        numStr = numStr.padStart(3, "0");
        return `${numStr.slice(0, 3)}`;
      } else {
        numStr = numStr.padStart(4, "0"); 
        return `${numStr.slice(0, 2)}.${numStr.slice(2, 4)}`;
      }
  };

  const [value, setValue] = useState<string>(() => {
      // Convert initialValue to a valid number if possible
      let numericValue = typeof initialValue === 'number' ? initialValue : parseFloat(initialValue as string);
      // Handle cases where initialValue is empty, zero, or invalid
      if (isNaN(numericValue)) {
        numericValue = 0;
      }
      return formatDecimal(isHeightCm ? numericValue : numericValue.toFixed(2), isHeightCm);
  });

  const stack = useRef<string[]>(isHeightCm ? ['0', '0', '0'] : ['0', '0', '0', '0']);

  const updateDisplay = () => {
      const formattedValue = isHeightCm
        ? `${stack.current[0]}${stack.current[1]}${stack.current[2]}`
        : `${stack.current[0]}${stack.current[1]}.${stack.current[2]}${stack.current[3]}`; 
    
      if (formattedValue !== value) {
        setValue(formattedValue);
      }
      if (onChange) 
      {   const fakeEvent = {
            target: { value: formattedValue },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(fakeEvent);
      }
  };
  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const key = event.key;
      // Allow only numeric keys
      if (!isNaN(Number(key)) && key !== " ") 
      {
          if (isHeightCm) {
            // 3-digit LIFO logic
            stack.current.push(key);
            while (stack.current.length > 3) {
              stack.current.shift(); // always keep latest 3 digits
            }
          } else {
            // 4-digit LIFO for non-height
            stack.current.push(key);
            while (stack.current.length > 4) {
              stack.current.shift();
            }
          }
          updateDisplay();
          event.preventDefault();
      }
  
      else if (key === "Backspace") 
      {
          if (isHeightCm) {
              stack.current.pop();
              stack.current.unshift('0');
              while (stack.current.length > 3) {
                stack.current = stack.current.slice(-3);
              }
          } else {
              stack.current.pop();
              stack.current.unshift('0');
              while (stack.current.length > 4) {
                stack.current = stack.current.slice(-4);
              }
          }
          updateDisplay();
          event.preventDefault();
      }
  };
  
  useEffect(() => {
      let numericValue = typeof initialValue === 'number' ? initialValue : parseFloat(initialValue as string);
      if (isNaN(numericValue)) numericValue = 0;
    
      const formatted = formatDecimal(isHeightCm ? numericValue : numericValue.toFixed(2), isHeightCm);
      setValue(formatted);
    
      const cleaned = formatted.replace(".", "").split("");
      if (cleaned.length === stack.current.length) {
        stack.current = cleaned;
      } else {
        stack.current = isHeightCm ? ['0', '0', '0'] : ['0', '0', '0', '0'];
      }
  }, [initialValue]);
  

  return (
    <InputContainer inputType={type}>
      <StyledInput
        ref={inputRef}
        type="text"
        value={value}
        onKeyDown={handleKeyDown}
        inputType={type}
        inputLength={value.length}
        maxLength={isHeightCm ? 3 : 5}
        {...props}
      />
    </InputContainer>
  );
};

export default NumberTextField;
